import React, { useState } from 'react';
import { Box, Button, Typography, Paper, TextField, Alert, CircularProgress } from '@mui/material';
import { getFirestore, doc, setDoc } from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { ROLES } from '../../firebase.marketplace';

/**
 * Component to directly create an admin user without requiring sign-in
 */
const DirectAdminCreator = () => {
  const [email, setEmail] = useState('kihembowilb@gmail.com');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState('');

  const handleCreateAdmin = async () => {
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const auth = getAuth();
      const db = getFirestore();
      
      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      setUserId(user.uid);
      
      // Create user document with admin roles
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: email.split('@')[0],
        roles: {
          [ROLES.USER]: true,
          [ROLES.ADMIN]: true,
          [ROLES.ARTIST]: true,
          superadmin: true
        },
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      setSuccess(true);
      console.log(`Successfully created admin user ${email} with ID: ${user.uid}`);
    } catch (err) {
      console.error('Error creating admin:', err);
      
      // Handle specific Firebase errors
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in with this email instead and use the Quick Admin Setup.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use a stronger password (at least 6 characters).');
      } else {
        setError(err.message || 'An unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom align="center">
          Create Admin User
        </Typography>
        
        <Typography variant="body1" paragraph>
          This tool creates a new user with admin privileges.
        </Typography>
        
        <TextField
          fullWidth
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          disabled={loading || success}
        />
        
        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          disabled={loading || success}
          helperText="Must be at least 6 characters"
        />
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button 
            variant="contained" 
            color="primary"
            disabled={loading || success || !email || !password}
            onClick={handleCreateAdmin}
          >
            {loading ? <CircularProgress size={24} /> : 'Create Admin User'}
          </Button>
        </Box>
        
        {success && (
          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body1">
              Successfully created admin user!
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              User ID: {userId}
            </Typography>
            <Typography variant="body2">
              Email: {email}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              You can now sign in with these credentials.
            </Typography>
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Instructions
        </Typography>
        
        <Typography variant="body2" paragraph>
          This tool creates a new user account with full admin privileges. Use this if you cannot sign in to an existing account.
        </Typography>
        
        <ol>
          <li>Enter the email address you want to use (default is kihembowilb@gmail.com)</li>
          <li>Enter a secure password (at least 6 characters)</li>
          <li>Click "Create Admin User"</li>
          <li>After successful creation, you can sign in with these credentials</li>
        </ol>
      </Paper>
    </Box>
  );
};

export default DirectAdminCreator;
